import { useState } from 'react';
import {
	Select,
	SelectItem,
	SelectItemProps,
	SelectPopover,
	SelectPopoverProps,
	SelectProps,
	SelectProvider,
	SelectProviderProps,
} from '@ariakit/react';
import DropdownUi, {
	DropdownUiTriggerProps,
} from 'src/components/primitives/DropdownUi/DropdownUi';
import Switch from 'src/components/primitives/Switch/Switch';
import { DROPDOWN_SIDE_OFFSET } from 'src/components/primitives/DropdownUi/consts';
import { cm } from 'src/utils/classNames';

interface RootProps extends SelectProviderProps {
	value: string[] | undefined;
	setValue: (value: string[]) => void;
	open?: boolean;
	onOpenChange?: (open: boolean) => void;
}

function Root({
	open: openFromProps,
	onOpenChange: onOpenChangeFromProps,
	value,
	setValue,
	...rest
}: RootProps) {
	const [openFromState, setOpenFromState] = useState(false);

	const open = openFromProps ?? openFromState;
	const onOpenChange = onOpenChangeFromProps ?? setOpenFromState;

	return (
		<SelectProvider
			value={value}
			setValue={setValue}
			open={open}
			setOpen={onOpenChange}
			{...rest}
		/>
	);
}

interface TriggerProps extends DropdownUiTriggerProps, SelectProps {}

function Trigger({
	size,
	className,
	labelClassName,
	valueClassName,
	label,
	disabled,
	children,
	...rest
}: TriggerProps) {
	return (
		<Select
			render={({ style, ...renderProps }) => (
				<DropdownUi.Trigger
					size={size}
					className={className}
					labelClassName={labelClassName}
					valueClassName={valueClassName}
					label={label}
					// Ariakit adds `pointer-events: none` to the style when the item is disabled.
					// But this means we can't show `cursor-not-allowed`. So we override it.
					style={{ ...style, pointerEvents: 'auto' }}
					{...renderProps}
				>
					{children}
					<DropdownUi.Chevron />
				</DropdownUi.Trigger>
			)}
			disabled={disabled}
			{...rest}
		/>
	);
}

interface BaseOptionsProps extends SelectPopoverProps {
	scrollContainerClassName?: string;
}

interface OptionsPropsWithNoSelectAllSwitch {
	noSelectAllSwitch: true;
	allSelected?: never;
	onAllSelectedChange?: never;
}

interface OptionsPropsWithSelectAllSwitch {
	noSelectAllSwitch?: false;
	allSelected: boolean;
	onAllSelectedChange: (allSelected: boolean) => void;
}

type OptionsProps = BaseOptionsProps &
	(OptionsPropsWithNoSelectAllSwitch | OptionsPropsWithSelectAllSwitch);

function Options({
	className,
	gutter = DROPDOWN_SIDE_OFFSET,
	scrollContainerClassName,
	noSelectAllSwitch,
	allSelected,
	onAllSelectedChange,
	children,
	...rest
}: OptionsProps) {
	return (
		<SelectPopover
			className={cm('min-w-(--popover-anchor-width)', className)}
			gutter={gutter}
			render={DropdownUi.Content}
			unmountOnHide
			sameWidth
			modal
			{...rest}
		>
			<div className="flex flex-col">
				{!noSelectAllSwitch && (
					<div className="flex items-center justify-between p-md typo-b4 text-text-default">
						<span>Select All</span>
						<Switch
							size="sm"
							checked={allSelected}
							onCheckedChange={onAllSelectedChange}
						/>
					</div>
				)}
				<div
					className={cm(
						'hidden-scroll flex flex-col overflow-y-auto',
						scrollContainerClassName
					)}
				>
					{children}
				</div>
			</div>
		</SelectPopover>
	);
}

interface OptionProps extends SelectItemProps {
	noSelectedCheckmark?: boolean;
	isSelected?: boolean;
}

function Option({
	children,
	disabled,
	noSelectedCheckmark,
	isSelected,
	...rest
}: OptionProps) {
	return (
		<SelectItem
			render={({ style, ...renderProps }) => (
				<DropdownUi.Item
					isSelected={isSelected}
					disabled={disabled}
					// Ariakit adds `pointer-events: none` to the style when the item is disabled.
					// But this means we can't show `cursor-not-allowed`. So we override it.
					style={{ ...style, pointerEvents: 'auto' }}
					{...renderProps}
				>
					{children}
					{isSelected && !noSelectedCheckmark && (
						<DropdownUi.SelectedCheckmark />
					)}
				</DropdownUi.Item>
			)}
			disabled={disabled}
			{...rest}
		/>
	);
}

export default { Root, Trigger, Options, Option };
